import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { formatKennitala } from 'app/helpers/formatKennitala';
import { formatPersonDetails } from 'app/helpers/formatPersonDetails';
import { ExposedListPublicRegistryCompaniesReq } from 'app/interfaces/requests/Exposed/exposedListPublicRegistryCompaniesReq';
import { ExposedListPublicRegistryPersonsReq } from 'app/interfaces/requests/Exposed/exposedListPublicRegistryPersonsReq';
import { ExposedListPublicRegistryCompaniesRes } from 'app/interfaces/responses/Exposed/exposedListPublicRegistryCompaniesRes';
import { ExposedListPublicRegistryPersonsRes } from 'app/interfaces/responses/Exposed/exposedListPublicRegistryPersonsRes';
import { ExposedReadPublicRegistryCompaniesRes } from 'app/interfaces/responses/Exposed/exposedReadPublicRegistryCompaniesRes';
import { ExposedReadPublicRegistryPersonsRes } from 'app/interfaces/responses/Exposed/exposedReadPublicRegistryPersonsRes';
import { PublicRegistryService } from 'app/services/api/publicregistry/publicregistry.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { Observable, Subscription, combineLatest, debounceTime, filter, finalize, forkJoin, fromEvent, map, tap } from 'rxjs';

interface PublicRegistrySearchOption {
    ssn: string;
    name: string;
    item: ExposedListPublicRegistryCompaniesRes | ExposedListPublicRegistryPersonsRes;
}

@Component({
    selector: 'public-registry-search',
    templateUrl: './public-registry-search.component.html',
    styleUrls: ['./public-registry-search.component.scss'],
})
export class PublicRegistrySearchComponent implements AfterViewInit {
    @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
    @ViewChild('matSearcher') matSearcher: ElementRef;
    @ViewChild('batchPaster') batchPaster: ElementRef;
    // Should the component automatically select the only option when a full ssn has been entered
    @Input() autoSelectFullSSN: boolean = true;
    // Should the component persist the selection after it has been made (e.g. showing the selected option in the input field)
    @Input() persistSelection: boolean = true;
    // Toggles between the single input tab and multiple input tab when adding to groups
    @Input() enableBatchInput: boolean = false;
    // Disables inputs if name and type fields are empty
    @Input() disableInput: boolean = false;
    @Input() label: string;
    @Input() pluralLabel: string;
    // Should the component search for persons
    @Input() searchForPersons: boolean = true;
    // Should the component search for companies
    @Input() searchForCompanies: boolean = false;
    // Subtitle to be displayed underneath the input field
    @Input() subtitle: string;
    // Determines if the input field should be displayed in an error state
    @Input() hasError: boolean = false;
    // Determines if the input field should be automatically focused when the loaded initially
    @Input() autofocus: boolean = false;
    @Output() invalidSsns = new EventEmitter<string[]>();
    @Output() onOptionSelected = new EventEmitter<ExposedListPublicRegistryPersonsRes | ExposedListPublicRegistryCompaniesRes>();

    loading: boolean = false;
    searchValue: string = '';
    batchResults = [];
    selectedPanelIndex = 0;
    selectedPanel: string = '';
    options: PublicRegistrySearchOption[] = [];
    selectedOption: PublicRegistrySearchOption = null;
    searchInputControl: FormControl = new FormControl();

    formatPersonDetails = formatPersonDetails;

    searchForm: FormGroup;

    // Controls
    get modeControl() {
        return this.searchForm.get('mode') as FormControl;
    }
    get singleControl() {
        return this.searchForm.get('single') as FormControl;
    }
    get batchControl() {
        return this.searchForm.get('batch') as FormControl;
    }

    get isSingle() {
        return this.modeControl.value === 'single';
    }
    get isBatch() {
        return this.modeControl.value === 'batch';
    }

    constructor(
        public translationService: TranslationService,
        private _formBuilder: UntypedFormBuilder,
        private _publicRegistryService: PublicRegistryService
    ) {
        // Build the form
        this.searchForm = this._formBuilder.group({
            mode: ['single'], // default to single
            single: [''],
            batch: [''],
        });
    }

    ngOnInit(): void {
        this.searchForm = this._formBuilder.group({
            mode: ['single', Validators.required],
            single: [''],
            batch: [''],
        });
    }

    ngAfterViewInit(): void {
        // Makes sure search is only triggered after 0.7 after the user has stopped typing
        fromEvent(this.matSearcher.nativeElement, 'input')
            .pipe(
                tap(() => {
                    // Clear the selection if the user starts typing again
                    if (this.selectedOption) {
                        this.clearSelection();
                        this.options = [];
                    }
                }),
                map((event: Event) => (event.target as HTMLInputElement).value),
                filter((value) => value && value.length >= 3),
                debounceTime(700)
            )
            .subscribe(() => this.search());

        if (this.autofocus) {
            // Timeout fixes ExpressionChangedAfterItHasBeenCheckedError console error
            setTimeout(() => {
                this.matSearcher.nativeElement.focus();
            });
        }
    }

    onBatchPaste() {
        setTimeout(() => {
            const raw = this.searchForm.get('batch').value || '';
            this.batchSearch(raw);
        });
    }

    private batchSearch(text: string) {
        this.batchControl.disable();
        this.loading = true;
        const SSN_REGEX = /\b(\d{6})-?(\d{4})\b/g;
        const matches = Array.from(text.matchAll(SSN_REGEX)).map((m) => m[1] + m[2]);
        const uniqueSsns = Array.from(new Set(matches));

        if (uniqueSsns.length == 0) {
            this.loading = false;
            this.batchControl.enable();
            return;
        }

        this._publicRegistryService
            .batchLookup(uniqueSsns)
            .pipe(
                finalize(() => {
                    this.loading = false;
                    this.batchControl.enable();
                })
            )
            .subscribe((items) => {
                // For each returned item, emit it exactly once:
                const foundSsns = items.map((i) => i.ssn);
                items.forEach((item) => {
                    const opt: PublicRegistrySearchOption = {
                        ssn: item.ssn,
                        name: item.name,
                        item: { ssn: item.ssn, name: item.name } as any,
                    };
                    this.optionSelected(opt);
                });
                const invalid = uniqueSsns.filter((ssn) => !foundSsns.includes(ssn));
                if (invalid.length > 0) {
                    // Puts invalid ssns back into the textarea for correction
                    this.batchControl.setValue(invalid.join('\n'));
                    // Let GroupsComponent know there are invalid ssns
                    this.invalidSsns.emit(invalid);
                } else {
                    this.batchControl.setValue(null);
                }
            });
    }

    tabChanged(event: MatTabChangeEvent) {
        if (event.index == 0) {
            this.selectedPanelIndex = 0;
        } else if (event.index == 1) {
            this.selectedPanelIndex = 1;
        }
    }

    search() {
        var value = this.matSearcher.nativeElement.value;

        // Check if the value contains a valid SSN, in which case we can directly select the user
        var ssn = this.checkForSSN(value);
        if (ssn) {
            this.exactSSNSearch(ssn);
        } else {
            // Search by the name and SSN of a person
            this.partialSearch(value);
        }
    }

    convertPersonToOptions = (
        person: ExposedReadPublicRegistryPersonsRes | ExposedListPublicRegistryPersonsRes
    ): PublicRegistrySearchOption => {
        return {
            ssn: person.ssn,
            name: person.name,
            item: person as ExposedListPublicRegistryPersonsRes,
        };
    };

    convertPersonsToOptions = (persons: ExposedListPublicRegistryPersonsRes[]): PublicRegistrySearchOption[] => {
        return persons.map((person) => {
            return this.convertPersonToOptions(person);
        });
    };

    convertCompanyToOptions = (
        company: ExposedReadPublicRegistryCompaniesRes | ExposedListPublicRegistryCompaniesRes
    ): PublicRegistrySearchOption => {
        return {
            ssn: company.idNumber,
            name: company.name,
            item: company as ExposedListPublicRegistryCompaniesRes,
        };
    };

    convertCompaniesToOptions = (companies: ExposedListPublicRegistryCompaniesRes[]): PublicRegistrySearchOption[] => {
        return companies.map((company) => {
            return this.convertCompanyToOptions(company);
        });
    };

    exactSubscription: Subscription;
    exactSSNSearch = (ssn: string) => {
        this.loading = true;

        var requests: {
            person?: Observable<ExposedReadPublicRegistryPersonsRes>;
            company?: Observable<ExposedReadPublicRegistryCompaniesRes>;
        } = {};

        if (this.searchForPersons) {
            requests.person = this._publicRegistryService.getPerson(ssn);
        }

        if (this.searchForCompanies) {
            requests.company = this._publicRegistryService.getCompany(ssn);
        }

        if (this.exactSubscription) {
            this.exactSubscription.unsubscribe();
        }

        this.exactSubscription = forkJoin(requests).subscribe((results) => {
            if (results.person) {
                this.options = [this.convertPersonToOptions(results.person)];
                if (this.autoSelectFullSSN) {
                    this.optionSelected(this.options[0]);
                    if (this.persistSelection) {
                        this.searchInputControl.setValue(this.options[0]);
                    }
                } else {
                    this.autocomplete.openPanel();
                }
            } else if (results.company) {
                this.options = [this.convertCompanyToOptions(results.company)];
                if (this.autoSelectFullSSN) {
                    this.optionSelected(this.options[0]);
                    if (this.persistSelection) {
                        this.searchInputControl.setValue(this.options[0]);
                    }
                } else {
                    this.autocomplete.openPanel();
                }
            }

            this.loading = false;
        });
    };

    partialSubscription: Subscription;
    partialSearch = (value: string) => {
        this.loading = true;

        var requests = {} as {
            namePersons: Observable<ExposedListPublicRegistryPersonsRes[]> | null;
            ssnPersons: Observable<ExposedListPublicRegistryPersonsRes[]> | null;
            nameCompanies: Observable<ExposedListPublicRegistryCompaniesRes[]> | null;
            ssnCompanies: Observable<ExposedListPublicRegistryCompaniesRes[]> | null;
        };

        if (this.searchForPersons) {
            requests.namePersons = this._publicRegistryService.listPersons(
                {
                    name: value,
                } as ExposedListPublicRegistryPersonsReq,
                false
            );

            requests.ssnPersons = this._publicRegistryService.listPersons(
                {
                    ssn: value,
                } as ExposedListPublicRegistryPersonsReq,
                false
            );
        }

        if (this.searchForCompanies) {
            requests.nameCompanies = this._publicRegistryService.listCompanies(
                {
                    Name: value,
                } as ExposedListPublicRegistryCompaniesReq,
                false
            );

            requests.ssnCompanies = this._publicRegistryService.listCompanies(
                {
                    IDNumber: value,
                } as ExposedListPublicRegistryCompaniesReq,
                false
            );
        }

        if (this.partialSubscription) {
            this.partialSubscription.unsubscribe();
        }

        this.partialSubscription = combineLatest(requests).subscribe((results) => {
            let options = [];

            if (results.namePersons) {
                options = options.concat(this.convertPersonsToOptions(results.namePersons));
            }

            if (results.ssnPersons) {
                options = options.concat(this.convertPersonsToOptions(results.ssnPersons));
            }

            if (results.nameCompanies) {
                options = options.concat(this.convertCompaniesToOptions(results.nameCompanies));
            }

            if (results.ssnCompanies) {
                options = options.concat(this.convertCompaniesToOptions(results.ssnCompanies));
            }

            options.map((option) => {
                option.ssn = formatKennitala(option.ssn);
            });

            this.options = options;
            if (options.length > 0) {
                this.autocomplete.openPanel();
            }
            this.loading = false;
        });
    };

    optionDisplayFn(option: ExposedListPublicRegistryPersonsRes | ExposedListPublicRegistryCompaniesRes): string {
        if (!option) {
            return '';
        }
        if ('ssn' in option) {
            return option ? formatPersonDetails(option.ssn, option.name) : '';
        } else {
            return option ? formatPersonDetails(option.idNumber, option.name) : '';
        }
    }

    optionSelected(option: PublicRegistrySearchOption) {
        this.autocomplete.closePanel();
        if (this.persistSelection) {
            this.selectedOption = option;
        } else {
            this.searchInputControl.setValue('');
        }
        this.onOptionSelected.emit(option.item);
    }

    clearSelection() {
        this.autocomplete?.closePanel();
        this.selectedOption = null;
        this.searchInputControl.reset();
        this.options = [];
    }

    // This function is used to check if the input is a valid SSN and returns the SSN if possible
    checkForSSN(value: string) {
        if (value && value.length >= 10) {
            let ssn = value.replace(/\D/g, '');
            if (ssn.length === 10) {
                return ssn;
            }
        }
    }
}
